import type { LinkPreviewCategory } from '../links/linkPreviewCategory'
import type { UnpackedCard } from '../screenshotCleanup/card'

/**
 * What a card is, said one way everywhere (design/studio/browse-card.css): a glyph drawn in the kind's hue, then its word. Never a tile, band
 * or chip. A link and an unpacked screenshot of the same kind share the hue. Colors live in tokens.css (`--studio-kind-*`); this names them.
 */
export type CardTone =
  | 'flight' | 'ride' | 'train' | 'event' | 'reservation' | 'hotel' | 'appointment'
  | 'recipe' | 'post' | 'article' | 'quote' | 'media' | 'reading' | 'shop' | 'place' | 'photo' | 'plain'

const LINK_TONES: Record<LinkPreviewCategory, CardTone> = {
  movie: 'media', audio: 'media', video: 'media', social: 'post', book: 'reading', event: 'event', reference: 'reading', document: 'reading',
  article: 'article', product: 'shop', place: 'place', code: 'reading', listing: 'shop', recipe: 'recipe', job: 'plain', link: 'plain',
}
export const linkTone = (category: LinkPreviewCategory): CardTone => LINK_TONES[category]

export function unpackedTone(card: UnpackedCard): CardTone {
  if (card.purpose === 'boarding-pass' || card.purpose === 'flight') return 'flight'
  if (card.purpose === 'ride') return 'ride'
  if (card.purpose === 'train') return 'train'
  if (card.purpose === 'hotel') return 'hotel'
  switch (card.kind) {
    case 'ticket': return 'event'
    case 'reservation': return 'reservation'
    case 'appointment': return 'appointment'
    case 'recipe': return 'recipe'
    case 'post': return 'post'
    case 'article': return 'article'
    case 'quote': return 'quote'
    default: return 'plain'
  }
}

const DAY_MS = 24 * 60 * 60 * 1000
const short = new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric' })

export interface WhenPill { text: string; state: 'soon' | 'later' | 'done' }

/** A booking's start as a few words: "Today", "Tomorrow", "In 3 days", "In 3 weeks", a date when far off, struck through when past. */
export function whenPill(startsAt: number | null | undefined, now: number): WhenPill | null {
  if (startsAt == null) return null
  const startOfDay = (time: number) => { const date = new Date(time); date.setHours(0, 0, 0, 0); return date.getTime() }
  const days = Math.round((startOfDay(startsAt) - startOfDay(now)) / DAY_MS)
  if (days < 0 || (days === 0 && startsAt < now - 3 * 60 * 60 * 1000)) return { text: short.format(startsAt), state: 'done' }
  if (days === 0) return { text: 'Today', state: 'soon' }
  if (days === 1) return { text: 'Tomorrow', state: 'soon' }
  if (days === 2) return { text: 'In 2 days', state: 'soon' }
  if (days < 14) return { text: `In ${days} days`, state: 'later' }
  if (days < 60) return { text: `In ${Math.round(days / 7)} weeks`, state: 'later' }
  return { text: short.format(startsAt), state: 'later' }
}
